/**
 * Format a byte count as a human-readable file size.
 *
 * @param bytes - File size in bytes. `null`/`undefined`/negative values yield `null`.
 * @returns A string like `"1.4 MB"`, or `null` when the size is unknown.
 *
 * @example
 * formatFileSize(0)        // '0 B'
 * formatFileSize(2048)     // '2 KB'
 * formatFileSize(1536000)  // '1.5 MB'
 * formatFileSize(null)     // null
 */
export function formatFileSize(bytes: number | null | undefined): string | null {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) {
    return null;
  }
  if (bytes === 0) {
    return '0 B';
  }

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const k = 1024;
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), units.length - 1);
  const value = bytes / k ** i;

  // Whole numbers (and bytes) read cleaner without decimals.
  const rounded = i === 0 || Number.isInteger(value) ? value : Math.round(value * 10) / 10;
  return `${rounded} ${units[i]}`;
}
